import React, { memo } from 'react'

import { Row, Column } from '@ublique/components/Grid'

import { DatePicker } from '@ublique/components/DatePicker'
import Checkbox from '@ublique/components/Checkbox'
import InfoButton from '../components/InfoButton'
import { useDateRange } from '../utils/daterange'
import { CRON_END, CRON_START } from 'components/CronScheduledStage/constants'
import {
  isInvalidByField,
  useCronExpForm
} from 'components/CronScheduledStage/utils/form'
import JobMonths from 'components/CronScheduledStage/components/JobMonths'
import JobDays from 'components/CronScheduledStage/components/JobDays'
import JobTime from 'components/CronScheduledStage/components/JobTime'

const Crontab = ({
  t,
  cronSettingsEnabled,
  enableCronSettings,
  cronStart,
  cronEnd,
  minutes,
  hours,
  months,
  weekdays,
  monthdays,
  timezone,
  setFieldValue,
  submitted,
  defaultScheduledData,
  isScheduledEditing
}) => {
  const { getHandleDateChange, today, cronStartValue, cronEndValue, locale } =
    useDateRange({ cronStart, cronEnd, setFieldValue })

  const { cronExp, editingValues, getHandleFieldChange } = useCronExpForm({
    disabled: !cronSettingsEnabled,
    minutes,
    hours,
    months,
    weekdays,
    monthdays,
    setFieldValue,
    defaultScheduledData
  })

  return (
    <>
      <Row className='line-title-row'>
        <hr />
        <Column sm={4} md={4}>
          <h5 className='creation-subtitle scheduling'>
            {t('Crontab.HEADING')}: {cronExp}
          </h5>
        </Column>
        <Column sm={3} md={3}>
          <Checkbox
            id='enable-crontab'
            defaultChecked={cronSettingsEnabled}
            onChange={(checked) => enableCronSettings(checked)}
            checked={cronSettingsEnabled}
            className='enable-crontab'
            labelText={t('Crontab.ENABLE_CRONTAB')}
          />
        </Column>
        <InfoButton message={t('Crontab.DATA_TOOLTIP')} />
      </Row>
      <Row>
        <Column sm={4} md={4}>
          <DatePicker
            dateFormat='Y/m/d'
            onChange={getHandleDateChange(CRON_START)}
            minDate={!isScheduledEditing && today}
            datePickerType='single'
            value={cronStartValue}
            locale={locale}
          >
            <DatePicker.Input
              id='cron-start-date-picker-single'
              labelText={t('Crontab.CRON_START')}
              invalid={isInvalidByField.cronStart({
                value: cronStart,
                submitted,
                disabled: !cronSettingsEnabled,
                values: { cronEnd },
                isScheduledEditing
              })}
              placeholder='yyyy/mm/dd'
              invalidText={t('Crontab.CRON_START_REQUIRED')}
              disabled={!cronSettingsEnabled}
            />
          </DatePicker>
        </Column>
      </Row>
      <Row className='line-title-row subtitle'>
        <hr />
        <Column>
          <h5 className='creation-subtitle scheduling'>
            {t('Crontab.MONTH_MANAGEMENT')}
          </h5>
        </Column>
      </Row>
      <Row>
        <Column sm={4} md={4} lg={6}>
          <JobMonths
            t={t}
            disabled={!cronSettingsEnabled}
            months={months}
            editingValues={editingValues}
            setFieldValue={setFieldValue}
          />
        </Column>
        <Column sm={4} md={4} lg={6}>
          <JobDays
            t={t}
            disabled={!cronSettingsEnabled}
            weekdays={weekdays}
            monthdays={monthdays}
            editingValues={editingValues}
            setFieldValue={setFieldValue}
          />
        </Column>
      </Row>
      <Row className='line-title-row subtitle'>
        <hr />
        <Column>
          <h5 className='creation-subtitle scheduling'>
            {t('Crontab.TIME_MANAGEMENT')}
          </h5>
        </Column>
      </Row>
      <JobTime
        t={t}
        disabled={!cronSettingsEnabled}
        minutes={minutes}
        hours={hours}
        timezone={timezone}
        getHandleFieldChange={getHandleFieldChange}
        editingValues={editingValues}
        setFieldValue={setFieldValue}
        submitted={submitted}
      />
      <Row>
        <Column sm={4} md={4}>
          <DatePicker
            dateFormat='Y/m/d'
            onChange={getHandleDateChange(CRON_END)}
            minDate={
              isScheduledEditing ? cronStartValue : cronStartValue || today
            }
            datePickerType='single'
            value={cronEndValue}
            locale={locale}
          >
            <DatePicker.Input
              id='cron-end-date-picker-single'
              labelText={t('Crontab.CRON_END')}
              invalid={isInvalidByField.cronEnd({
                value: cronEnd,
                submitted,
                disabled: !cronSettingsEnabled,
                values: { cronStart },
                isScheduledEditing
              })}
              placeholder='yyyy/mm/dd'
              invalidText={t('Crontab.CRON_END_REQUIRED')}
              disabled={!cronSettingsEnabled}
            />
          </DatePicker>
        </Column>
      </Row>
    </>
  )
}

export default memo(Crontab)
